/**
 * @file settings-api.ts
 * @module app/modules/setting/utils/settings-api
 * @description Settings feature API fetch helpers.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.5
 * @version 2.1.2
 */

import { platformSettings } from '../../../../api/apiEndPoints';
import { callApiWithToast } from '../../../../utils/apiCallWithToast';
import { Settings as SettingsPermission } from '../../../../utils/constants';
import { IConfigurationRecord } from '../../../../types/common/common.types';

export type { IConfigurationRecord };

/**
 * Calls the settings API and returns the configuration record list.
 * @param {string} [warehouseId] Warehouse scope for non-global settings.
 * @returns {Promise<IConfigurationRecord[]>} Configuration records array.
 */
export const fetchConfigurationRecords = async (
  warehouseId?: string,
): Promise<IConfigurationRecord[]> => {
  const response = await callApiWithToast({
    endpoint: platformSettings.getSettings,
    params: {},
    module: SettingsPermission,
    warehouse: warehouseId,
  });

  if (!response) {
    return [];
  }

  const recordPayload = Array.isArray(response?.record)
    ? response.record[0]
    : response?.record;

  return (
    recordPayload?.configuration?.records ||
    recordPayload?.data?.configuration?.records ||
    []
  );
};
